import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  BookOpen, Heart, Camera, Calendar, Star, Quote, Users,
  Plus, Save, Share2, Download, Trash2, Edit2, Clock,
  Image, Video, Music, FileText, X
} from 'lucide-react';

const MEMORY_TYPES = [
  { value: 'story', label: 'Story', icon: BookOpen, color: 'text-blue-500' },
  { value: 'photo_caption', label: 'Photo Memory', icon: Camera, color: 'text-green-500' },
  { value: 'milestone', label: 'Milestone', icon: Star, color: 'text-yellow-500' },
  { value: 'gratitude', label: 'Gratitude', icon: Heart, color: 'text-pink-500' },
  { value: 'quote', label: 'Favorite Quote', icon: Quote, color: 'text-purple-500' },
  { value: 'tradition', label: 'Family Tradition', icon: Users, color: 'text-orange-500' },
];

export function LegacyMemoriesJournal({ patientId, patientName = "Your Loved One" }) {
  const [memories, setMemories] = useState([
    {
      id: '1',
      title: 'First Family Vacation',
      content: 'We drove all the way to the beach in that old station wagon. Dad sang silly songs the whole way. Mom packed sandwiches that got a little squished but tasted like heaven.',
      memory_type: 'story',
      date_of_memory: '1985-07-15',
      tags: ['vacation', 'beach', 'family'],
      created_at: new Date().toISOString(),
    },
    {
      id: '2',
      title: 'Sunday Dinners',
      content: 'Every Sunday, the whole family would gather around the big oak table. Grandma\'s pot roast, mashed potatoes, and that apple pie that filled the whole house with the most wonderful smell.',
      memory_type: 'tradition',
      date_of_memory: null,
      tags: ['family', 'food', 'tradition'],
      created_at: new Date().toISOString(),
    },
  ]);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [newMemory, setNewMemory] = useState({
    title: '',
    content: '',
    memory_type: 'story',
    date_of_memory: '',
    tags: ''
  });

  const handleAddMemory = () => {
    if (!newMemory.title || !newMemory.content) {
      toast.error('Please fill in the title and memory content');
      return;
    }

    const memory = {
      id: Date.now().toString(),
      title: newMemory.title,
      content: newMemory.content,
      memory_type: newMemory.memory_type,
      date_of_memory: newMemory.date_of_memory || null,
      tags: newMemory.tags.split(',').map(t => t.trim()).filter(Boolean),
      created_at: new Date().toISOString(),
    };

    setMemories(prev => [memory, ...prev]);
    setNewMemory({ title: '', content: '', memory_type: 'story', date_of_memory: '', tags: '' });
    setShowAddDialog(false);
    toast.success('Memory saved to your legacy journal');
  };

  const handleDeleteMemory = (id) => {
    setMemories(prev => prev.filter(m => m.id !== id));
    toast.success('Memory removed');
  };

  const handleExportPDF = () => {
    toast.success('Generating PDF of your memories...');
    // In production, this would generate a beautiful PDF
  };

  const getMemoryTypeInfo = (type) => {
    return MEMORY_TYPES.find(t => t.value === type) || MEMORY_TYPES[0];
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6" data-testid="legacy-memories-journal">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">
            Legacy Memories Journal
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Preserve and celebrate the precious moments of {patientName}'s life
          </p>
        </div>

        {/* Action Bar */}
        <div className="flex flex-wrap gap-3 justify-center mb-8">
          <Button onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="add-memory-btn">
            <Plus className="w-4 h-4" />
            Add Memory
          </Button>
          <Button variant="outline" onClick={handleExportPDF} className="gap-2">
            <Download className="w-4 h-4" />
            Export PDF Book
          </Button>
          <Button variant="outline" className="gap-2">
            <Share2 className="w-4 h-4" />
            Share with Family
          </Button>
        </div>

        {/* Memory Type Filters */}
        <div className="flex flex-wrap gap-2 justify-center mb-6">
          {MEMORY_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <Badge 
                key={type.value} 
                variant="outline" 
                className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 px-3 py-1"
              >
                <Icon className={`w-3 h-3 mr-1 ${type.color}`} />
                {type.label}
              </Badge>
            );
          })}
        </div>

        {/* Memories Grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {memories.map(memory => {
            const typeInfo = getMemoryTypeInfo(memory.memory_type);
            const TypeIcon = typeInfo.icon;
            
            return (
              <Card 
                key={memory.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedMemory(memory)}
                data-testid={`memory-${memory.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-full bg-slate-100 dark:bg-slate-800`}>
                        <TypeIcon className={`w-4 h-4 ${typeInfo.color}`} />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{memory.title}</CardTitle>
                        <p className="text-xs text-muted-foreground">
                          {typeInfo.label}
                          {memory.date_of_memory && ` • ${new Date(memory.date_of_memory).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMemory(memory.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-3">
                    {memory.content}
                  </p>
                  {memory.tags && memory.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {memory.tags.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {memories.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Heart className="w-12 h-12 mx-auto text-pink-400 mb-4" />
              <h3 className="text-lg font-medium mb-2">Start Your Legacy Journal</h3>
              <p className="text-muted-foreground mb-4">
                Every memory matters. Begin capturing the beautiful moments that make {patientName}'s story unique.
              </p>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Memory
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add Memory Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-500" />
                Add a Memory
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Memory Type</Label>
                <Select
                  value={newMemory.memory_type}
                  onValueChange={(value) => setNewMemory(prev => ({ ...prev, memory_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEMORY_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="flex items-center gap-2">
                          <type.icon className={`w-4 h-4 ${type.color}`} />
                          {type.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  placeholder="Give this memory a title..."
                  value={newMemory.title}
                  onChange={(e) => setNewMemory(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>The Memory</Label>
                <Textarea
                  placeholder="Share the details of this special memory..."
                  rows={5}
                  value={newMemory.content}
                  onChange={(e) => setNewMemory(prev => ({ ...prev, content: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>When did this happen?</Label>
                  <Input
                    type="date"
                    value={newMemory.date_of_memory}
                    onChange={(e) => setNewMemory(prev => ({ ...prev, date_of_memory: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tags (comma separated)</Label>
                  <Input
                    placeholder="family, vacation, funny"
                    value={newMemory.tags}
                    onChange={(e) => setNewMemory(prev => ({ ...prev, tags: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddMemory}>
                <Save className="w-4 h-4 mr-2" />
                Save Memory
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Memory Dialog */}
        <Dialog open={!!selectedMemory} onOpenChange={() => setSelectedMemory(null)}>
          {selectedMemory && (
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{selectedMemory.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {(() => {
                    const typeInfo = getMemoryTypeInfo(selectedMemory.memory_type);
                    const TypeIcon = typeInfo.icon;
                    return (
                      <>
                        <TypeIcon className={`w-4 h-4 ${typeInfo.color}`} />
                        <span>{typeInfo.label}</span>
                      </>
                    );
                  })()}
                  {selectedMemory.date_of_memory && (
                    <>
                      <span>•</span>
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(selectedMemory.date_of_memory).toLocaleDateString()}</span>
                    </>
                  )}
                </div>
                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {selectedMemory.content}
                </p>
                {selectedMemory.tags && selectedMemory.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedMemory.tags.map(tag => (
                      <Badge key={tag} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedMemory(null)}>
                  Close
                </Button>
                <Button variant="outline" className="gap-2">
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
              </DialogFooter>
            </DialogContent>
          )}
        </Dialog>
      </div>
    </div>
  );
}

export default LegacyMemoriesJournal;
